/**
 * Main App Layout
 */

import React, { useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import { Header } from './components/Header';
import { MetricsBar } from './components/MetricsBar';
import { AgentList } from './components/AgentList';
import { Charts } from './components/Charts';
import { CacheStats } from './components/CacheStats';
import { EventTimeline } from './components/EventTimeline';
import { DebuggerPanel } from './components/DebuggerPanel';
import { NeuralSearch } from './components/NeuralSearch';
import { McpLiveFeed } from './components/McpLiveFeed';
import { SavingsBreakdownWidget } from './components/SavingsBreakdownWidget';
import { useAPI } from './hooks/useAPI';
import { useDashboardStore } from './store/dashboardStore';

const App: React.FC = () => {
  const {
    fetchMetrics,
    fetchAgents,
    fetchEvents,
    fetchCacheStats,
    fetchSavingsBreakdown,
  } = useAPI({ enableSocket: true });
  const refreshIntervalSeconds = useDashboardStore(
    (state) => state.refreshIntervalSeconds
  );

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        fetchMetrics(),
        fetchAgents(),
        fetchEvents(),
        fetchCacheStats(),
        fetchSavingsBreakdown(),
      ]);
    };

    loadData();

    const intervalMs = refreshIntervalSeconds * 1000;
    const refreshTimer = setInterval(() => {
      fetchMetrics();
      fetchAgents();
      fetchEvents();
      fetchCacheStats();
      fetchSavingsBreakdown();
    }, intervalMs);

    return () => {
      clearInterval(refreshTimer);
    };
  }, [fetchMetrics, fetchAgents, fetchEvents, fetchCacheStats, fetchSavingsBreakdown, refreshIntervalSeconds]);

  const activeSidePanel = useDashboardStore((state) => state.activeSidePanel);
  const metrics = useDashboardStore((state) => state.metrics);

  return (
    <div className="h-screen bg-transparent flex flex-col overflow-hidden text-slate-200">
      {/* Header */}
      <Header />

      {/* KPI Metrics Strip */}
      <div className="px-5 py-3 bg-slate-900/20 backdrop-blur-md border-b border-white/5 flex-shrink-0">
        <MetricsBar />
      </div>

      {/* Pro Upgrade Banner — only for free tier */}
      {metrics && metrics.tier !== 'pro' && (
        <div className="px-5 py-2.5 bg-gradient-to-r from-amber-500/5 via-amber-500/10 to-amber-500/5 border-b border-amber-500/20 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-lg">💎</span>
              <div>
                <p className="text-xs font-bold text-amber-300 uppercase tracking-wider">
                  You&apos;re on the Free Plan
                </p>
                <p className="text-[10px] text-amber-500/80 mt-0.5">
                  Unlock semantic cache, cloud sync, autopilot healer, unlimited requests & more
                </p>
              </div>
            </div>
            <a
              href="https://lemma.nxus.studio/upgrade"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2 text-[11px] font-black uppercase tracking-widest text-white bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 rounded-xl transition-all duration-200 shadow-lg shadow-amber-500/20"
            >
              Upgrade · $12/mo
            </a>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 overflow-hidden flex gap-4 p-4 min-h-0">
        {activeSidePanel === 'brain' ? (
          <div className="flex-1 panel-card overflow-hidden">
            <NeuralSearch />
          </div>
        ) : activeSidePanel === 'tools' ? (
          <div className="flex-1 panel-card overflow-hidden">
            <McpLiveFeed />
          </div>
        ) : (
          <>
            {/* Left Panel — Agent List */}
            <div className="w-72 flex-shrink-0 overflow-hidden panel-card">
              <AgentList />
            </div>

            {/* Center Panel — Charts only (full height) */}
            <div className="flex-1 overflow-y-auto space-y-4 pb-2 pr-0.5 min-h-0">
              <Charts />
            </div>

            {/* Right Panel — Savings Breakdown + Cache + Events */}
            <div className="flex-shrink-0 flex flex-col gap-4 min-h-0" style={{ width: '360px' }}>
              {/* Savings Breakdown Widget */}
              <div className="panel-card flex-shrink-0">
                <SavingsBreakdownWidget />
              </div>

              {/* Cache Stats */}
              <div
                className="panel-card"
                style={{ height: '30%' }}
              >
                <CacheStats />
              </div>

              {/* Event Timeline */}
              <div className="flex-1 panel-card">
                <EventTimeline />
              </div>
            </div>
          </>
        )}
      </div>

      {/* Developer Tools — collapsible bottom panel */}
      <DebuggerPanel />

      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: '#1e293b',
            color: '#f1f5f9',
            borderRadius: '12px',
            border: '1px solid rgba(255,255,255,0.08)',
            fontSize: '13px',
            fontWeight: '600',
          },
          success: { iconTheme: { primary: '#10b981', secondary: '#fff' } },
          error: { iconTheme: { primary: '#ef4444', secondary: '#fff' } },
        }}
      />
    </div>
  );
};

export default App;
